import React from 'react';
import { useTranslation } from 'react-i18next';
import useDialogFocus from '../../utils/useDialogFocus';
import { countryDisplayName } from '../../utils/countryDisplayName';
import './ds-primitives.css';

const STATUS_LABEL_KEYS = {
  A: 'designSystem.statusAvailable',
  B: 'designSystem.statusPendingPublication',
  C: 'designSystem.statusTemporarilyUnavailable',
  D: 'designSystem.statusIntegrationPending',
  E: 'designSystem.statusNotApplicable',
};

/** Data status A–E (Design v3 data-states.md). */
export function StatusChip({ status = 'A', label, className = '' }) {
  const { t } = useTranslation();
  const code = String(status).toUpperCase();
  if (code === 'E') return null;
  const defaultLabel = STATUS_LABEL_KEYS[code] ? t(STATUS_LABEL_KEYS[code]) : code;
  return (
    <span className={`ds-status-chip ds-status-chip--${code} ${className}`.trim()}>
      {label || defaultLabel}
    </span>
  );
}

const COVERAGE_LABEL_KEYS = {
  full: 'designSystem.coverageFull',
  partial: 'designSystem.coveragePartial',
  benchmark: 'designSystem.coverageBenchmark',
  planned: 'designSystem.coveragePlanned',
};

export function CoverageBadge({ tier = 'partial' }) {
  const { t } = useTranslation();
  const tierKey = String(tier).toLowerCase();
  const labelKey = COVERAGE_LABEL_KEYS[tierKey];
  return (
    <span className={`ds-coverage-badge ds-coverage-badge--${tierKey}`}>
      {labelKey ? t(labelKey) : tier}
    </span>
  );
}

/**
 * Compact Data Display Contract.
 * value + observationPeriod + delta + source + temporalStatus
 */
export function DataPoint({
  label,
  value,
  observationPeriod,
  publishedAt: _publishedAt, // accepted but not rendered (period + source only)
  delta,
  deltaDirection, // 'up' | 'down' | 'neutral'
  source,
  temporalStatus,
  status = 'A',
  indicatorId,
}) {
  // Never render warehouse ids (gold:AR_…) under the value.
  const safeSource =
    source && !/^gold[:.]/i.test(String(source).trim()) && !/^[A-Z]{2}_[A-Z0-9_]+$/i.test(String(source).trim())
      ? source
      : undefined;
  if (status === 'E') return null;

  if (status !== 'A') {
    return (
      <article className="ds-datapoint">
        <span className="ds-datapoint__label">{label}</span>
        <StatusChip status={status} />
        {(safeSource || temporalStatus) && (
          <div className="ds-datapoint__meta">
            {[safeSource, temporalStatus].filter(Boolean).join(' · ')}
          </div>
        )}
      </article>
    );
  }

  const deltaClass =
    deltaDirection === 'up'
      ? 'ds-datapoint__delta--up'
      : deltaDirection === 'down'
        ? 'ds-datapoint__delta--down'
        : '';

  // Keep meta to source / temporal status only — "Publicado" dates clutter KPIs
  // without adding decision value on public and topic surfaces.
  const metaLine = [safeSource, temporalStatus].filter(Boolean).join(' · ');

  return (
    <article className="ds-datapoint">
      <span className="ds-datapoint__label">{label}</span>
      <div className="ds-datapoint__value">{value}</div>
      {observationPeriod ? <div className="ds-datapoint__period">{observationPeriod}</div> : null}
      {delta != null && delta !== '' ? (
        <div className={`ds-datapoint__delta ${deltaClass}`.trim()}>{delta}</div>
      ) : null}
      {metaLine ? <div className="ds-datapoint__meta">{metaLine}</div> : null}
    </article>
  );
}

export function MetricStrip({ children, className = '', style }) {
  return (
    <div className={`ds-metric-strip ${className}`.trim()} style={style}>
      {children}
    </div>
  );
}

export const G6_ECONOMIES = [
  { iso: 'US', name: 'Estados Unidos' },
  { iso: 'EA', name: 'Zona euro' },
  { iso: 'BR', name: 'Brasil' },
  { iso: 'IN', name: 'India' },
  { iso: 'JP', name: 'Japón' },
  { iso: 'AR', name: 'Argentina' },
];

export function FocusEconomySelect({
  value = 'AR',
  onChange,
  economies = G6_ECONOMIES,
  label = 'Economía de enfoque',
  language,
}) {
  return (
    <label className="ds-focus-select">
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        aria-label={label}
      >
        {economies.map((e) => (
          <option key={e.iso} value={e.iso}>
            {language ? countryDisplayName(e.iso, language, e.name) : e.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Symmetric G6 hub rows. Rows without KPIs (planned) go to footer list.
 */
export function GlobalEconomyStrip({
  rows = [],
  planned = [],
  onSelect,
  plannedLabel = 'Planificados',
  viewCtaLabel = 'Ver →',
  emptyHint,
}) {
  return (
    <div className="ds-g6-strip" role="list">
      {rows.length === 0 && emptyHint ? (
        <p className="ds-g6-empty-hint" role="status">
          {emptyHint}
        </p>
      ) : null}
      {rows.map((row) => (
        <button
          type="button"
          key={row.iso}
          className="ds-g6-row"
          role="listitem"
          onClick={() => onSelect?.(row.iso)}
        >
          <span className="ds-g6-row__name">{row.name}</span>
          <span className="ds-g6-row__kpis">{row.summary || '—'}</span>
          <CoverageBadge tier={row.coverage || 'benchmark'} />
          <span className="ds-g6-row__cta">{viewCtaLabel}</span>
        </button>
      ))}
      {planned.length > 0 ? (
        <div className="ds-g6-planned">
          <span className="ds-g6-planned__label">{plannedLabel}</span>
          <ul className="ds-g6-planned__list">
            {planned.map((p) => (
              <li key={p.iso} className="ds-g6-planned__item">
                <button
                  type="button"
                  className="ds-g6-planned__link"
                  onClick={() => onSelect?.(p.iso)}
                >
                  {p.name}
                </button>
                <StatusChip status={p.status || 'D'} label={p.note} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function DsButton({
  variant = 'primary',
  size = 'md',
  children,
  className = '',
  type = 'button',
  ...rest
}) {
  const sizeClass = size === 'sm' ? 'ds-btn--sm' : '';
  return (
    <button
      type={type}
      className={`ds-btn ds-btn--${variant} ${sizeClass} ${className}`.trim()}
      {...rest}
    >
      {children}
    </button>
  );
}

export function EditorialSurface({ children, className = '' }) {
  return <div className={`ds-surface ds-surface--editorial ${className}`.trim()}>{children}</div>;
}

export function TerminalSurface({ children, className = '' }) {
  return <div className={`ds-surface ds-surface--terminal ${className}`.trim()}>{children}</div>;
}

export const SoftwareSurface = React.forwardRef(function SoftwareSurface(
  { children, className = '', ...rest },
  ref
) {
  return (
    <div
      ref={ref}
      className={`ds-surface ds-surface--software ${className}`.trim()}
      {...rest}
    >
      {children}
    </div>
  );
});

export function EditorialLede({ children }) {
  return <p className="ds-editorial-lede">{children}</p>;
}

/** Right-side tools drawer (Research reader insights / export helpers). */
export function Drawer({ open, onClose, title, children, side = 'right' }) {
  const { t } = useTranslation();
  const dialogRef = useDialogFocus(open, onClose);

  if (!open) return null;

  return (
    <div className="ds-drawer-root" role="presentation">
      <div className="ds-drawer-backdrop" aria-hidden="true" onClick={onClose} />
      <aside
        ref={dialogRef}
        className={`ds-drawer ds-drawer--${side}`}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Panel'}
        tabIndex={-1}
      >
        <div className="ds-drawer__header">
          <h2 className="ds-drawer__title">{title}</h2>
          <button type="button" className="ds-btn ds-btn--text" onClick={onClose}
            aria-label={t('common.close', { defaultValue: 'Cerrar' })} data-dialog-initial-focus>
            ×
          </button>
        </div>
        <div className="ds-drawer__body">{children}</div>
      </aside>
    </div>
  );
}
